<template>
  <SubletPage :title="title">
    <slot name="collapse-card" />
    <Summary
      v-if="!applicationIsEditable && !documentsRejected"
      category="DOCUMENTS"
      class="documents-view-wrapper"
      :show-change-btn="false"
    >
      <SummaryBody
        v-if="documentsForSelectedReason.length > 0"
        sub-heading="Uppladdade filer"
        class="documents-subheading"
      >
        <SubletDocumentsGrid
          :documents="documentsForSelectedReason"
          :application-id="application.id"
        />
      </SummaryBody>
    </Summary>
    <div v-else class="upload-area">
      <p>* <i>Obligatoriskt att ladda upp</i></p>
      <div
        v-for="{ category, required } in activeCategoriesForUser"
        :key="category"
      >
        <OvDocumentsUpload
          :application="application"
          :documents-rejected="documentsRejected"
          :label="getLabel(category, required)"
          :descriptions="descriptions"
          :document-category="category"
          :category-type="categoryTypes[0]"
          :documents="getFilteredDocuments(category)"
          :computed-documents-groups="computedDocumentsGroups"
        />
        <CollapseCard
          v-for="(text, index) in getDocumentInfoTexts(category).filter(
            n => !!n.title && !!n.content
          )"
          :key="index"
          is-primary
          initially-open
          class="mb-4"
          :closed-identifier="`portion-${category}-${index}`"
        >
          <h3 slot="title-text">{{ text.title }}</h3>
          <template slot="body-text">
            <div class="info-text-container">
              <p v-html="text.content" />
            </div>
          </template>
        </CollapseCard>
      </div>
    </div>
    <BaseButton
      class="go-to-next-category-btn"
      type="button"
      :is-rounded="true"
      :is-fullwidth="isMobile"
      :is-disabled="!applicationIsEditable && !isMobile"
      :is-small="isMobile"
      @click="$emit('goToNextCategory')"
      ><BaseIconText :icon-url="svgs.ICONS.ARROW_RIGHT" :align-right="true">
        Gå vidare
      </BaseIconText>
    </BaseButton>
  </SubletPage>
</template>

<script>
import { mapGetters } from 'vuex';
import SubletPage from '@/components/SubletPage.vue';
import OvDocumentsUpload from '@/components/OvDocumentsUpload.vue';
import CollapseCard from '@/components/CollapseCard.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseIconText from '@/components/BaseIconText.vue';
import Summary from '@/components/summary/Summary.vue';
import SummaryBody from '@/components/summary/SummaryBody.vue';
import icons from '@/utils/icon-constants';
import svgs from '@/utils/icon-constants';
import SubletDocumentsGrid from '@/components/SubletDocumentsGrid.vue';

export default {
  name: 'AhOvDocuments',
  components: {
    SubletPage,
    OvDocumentsUpload,
    CollapseCard,
    BaseButton,
    BaseIconText,
    Summary,
    SummaryBody,
    SubletDocumentsGrid
  },
  props: {
    title: {
      type: String,
      required: true
    },
    application: {
      type: Object,
      required: true
    },
    computedDocumentsGroups: {
      type: Object,
      required: true
    },
    categoryTypes: {
      type: Array,
      required: true
    },
    applicationIsEditable: {
      type: Boolean,
      required: true
    },
    rejectedCategories: {
      type: Array,
      required: true
    },
    isPrivateFlow: {
      type: Boolean,
      default: false
    },
    documentsPerCategory: {
      type: Array,
      required: true
    },
    descriptions: {
      type: Object,
      required: true
    },
    documentInfoTexts: {
      type: Object,
      required: true
    },
    labels: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      icons,
      svgs
    };
  },
  computed: {
    ...mapGetters({
      getSupportAddress: 'app/getSupportAddress',
      isMobile: 'app/isMobile'
    }),
    applicationType() {
      return this.application?.applicationType;
    },
    documentGroupsKeys() {
      if (!this.computedDocumentsGroups) {
        return [];
      }
      return Object.keys(this.computedDocumentsGroups);
    },
    documentsRejected() {
      if (
        this.categoryTypes.includes('transferee') ||
        this.categoryTypes.includes('secondary_tenant')
      ) {
        return (
          this.rejectedCategories.includes('invitee_documents') ||
          this.rejectedCategories.includes('inviteeComputedDocumentsGroups')
        );
      }
      return (
        this.rejectedCategories.includes('owner_documents') ||
        this.rejectedCategories.includes('documents') ||
        this.rejectedCategories.includes('computedDocumentsGroups')
      );
    },
    categories() {
      const categories = Object.keys(this.computedDocumentsGroups).map(key => ({
        rejectReason: this.computedDocumentsGroups[key].rejectReason,
        status: this.computedDocumentsGroups[key].status,
        category: key
      }));
      return [...this.documents, ...categories];
    },
    documents() {
      return this.application?.documents || [];
    },
    reason() {
      return this.application?.reason;
    },
    reasonType() {
      return this.application?.reason?.type;
    },

    documentsForSelectedReason() {
      const documentsToShow = this.documentsPerCategory;
      const filteredDocuments = this.documents.filter(
        doc =>
          documentsToShow.includes(doc.category) &&
          this.categoryTypes.includes(doc.categoryType)
      );

      return filteredDocuments;
    },
    activeCategoriesForUser() {
      if (!Object.keys(this.computedDocumentsGroups).length) {
        return null;
      }
      return Object.entries(this.computedDocumentsGroups)
        .map(([key, props]) => ({
          category: key,
          required: props.required
        }))
        .sort((a, b) => {
          if (b.required !== a.required) {
            return b.required - a.required;
          }

          return a.category.localeCompare(b.category);
        });
    }
  },
  methods: {
    getDocumentInfoTexts(category) {
      const text = this.documentInfoTexts[category];
      return text || [{ title: '', content: '' }];
    },
    getFilteredDocuments(category) {
      if (!category || this.documents.length === 0) {
        return [];
      }

      const currDocumentCat = this.documents.filter(
        document =>
          document.category === category &&
          this.categoryTypes.includes(document.categoryType)
      );

      return currDocumentCat;
    },
    getLabel(category, required) {
      const reqSymbol = required ? '*' : '';
      let text = this.labels[category] || category;
      text = `${text}${reqSymbol}`;
      return text;
    }
  }
};
</script>

<style lang="scss" scoped>
a {
  &:hover {
    color: var(--color-blue);
  }
}

.upload-area > * {
  margin-bottom: 3.2rem;
  &:nth-last-child(1) {
    margin-bottom: 0;
  }
}

.go-to-next-category-btn {
  margin-top: 4.8rem;
}

.documents-subheading {
  @include mobile {
    display: flex;
    align-items: center !important;
    width: 100%;
  }
}
</style>
